// btw-front/src/pages/PartnerEdit.jsx
// 파트너 정보 수정 — 영업대표 N명 / 기술지원 N명 관리. + 버튼으로 추가, X로 삭제.
// 영업대표는 계정별 목록이다 — 이 화면에는 로그인한 계정의 영업대표만 나온다(같은 회사 다른 계정 것은 안 보임).
// 가입 때 등록된 행도 다른 행과 같이 수정·삭제할 수 있다(기본 행 개념 없음).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import {
  listSalesReps,
  createSalesRep,
  updateSalesRep,
  deleteSalesRep,
  listTechSupports,
  createTechSupport,
  updateTechSupport,
  deleteTechSupport,
} from "../api/partnerReps";
import { getMe, updateMyCompany } from "../api/partnerDeals";
import { formatPhone } from "../lib/format";
import { applySavedOutcomes } from "../lib/partnerRepRows";

let _localKeyCounter = 0;
function newLocalKey() {
  return `new-${++_localKeyCounter}`;
}

function toRow(item) {
  return {
    rowKey: String(item.id),
    id: String(item.id),
    name: item.name || "",
    title: item.title || "",
    phone: item.phone || "",
    email: item.email || "",
    _new: false,
    _dirty: false,
    _deleted: false,
  };
}
function emptyRow() {
  return {
    rowKey: newLocalKey(),
    id: null,
    name: "",
    title: "",
    phone: "",
    email: "",
    _new: true,
    _dirty: false,
    _deleted: false,
  };
}

// 회사 규모·회사 이메일 폼 — 빈 문자열은 미입력. 저장 시 null 로 변환.
const EMPTY_COMPANY = { salesRepHeadcount: "", techSupportHeadcount: "", companyEmail: "" };
// 가입 시 등록된 기본 정보
const EMPTY_BASICS = {
  companyName: "",
  businessNumber: "",
  representativeName: "",
  industry: "",
  address: "",
};
function headcountToServer(v) {
  return v === "" ? null : Number(v);
}
const EMAIL_RE = /^\S+@\S+\.\S+$/;

export default function PartnerEdit() {
  const { t } = useTranslation();
  const [salesReps, setSalesReps] = useState([]);
  const [techSupports, setTechSupports] = useState([]);
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [basics, setBasics] = useState(EMPTY_BASICS);
  // 로드 시점 회사 이메일 — 원래 빈 값(NULL 레거시)이면 이메일 입력 없이도
  // 인원수 수정을 허용하기 위해 기억해 둔다
  const [initialCompanyEmail, setInitialCompanyEmail] = useState("");
  const [companyDirty, setCompanyDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const [sr, ts, me] = await Promise.all([listSalesReps(), listTechSupports(), getMe()]);
      setSalesReps(sr.map(toRow));
      setTechSupports(ts.map(toRow));
      const p = (me && me.partner) || {};
      setCompany({
        salesRepHeadcount: p.salesRepHeadcount == null ? "" : String(p.salesRepHeadcount),
        techSupportHeadcount: p.techSupportHeadcount == null ? "" : String(p.techSupportHeadcount),
        companyEmail: p.companyEmail || "",
      });
      setInitialCompanyEmail((p.companyEmail || "").trim());
      setBasics({
        companyName: p.companyName || "",
        businessNumber: p.businessNumber || "",
        representativeName: p.representativeName || "",
        industry: p.industry || "",
        address: p.address || "",
      });
      setCompanyDirty(false);
    } catch (e) {
      message.error(t("partners.edit.loadFailed"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setCompanyField(key, val) {
    setCompany((c) => ({ ...c, [key]: val }));
    setCompanyDirty(true);
  }

  function updateRow(setter, rowKey, patch) {
    setter((rows) =>
      rows.map((r) =>
        r.rowKey === rowKey ? { ...r, ...patch, _dirty: r._new ? r._dirty : true } : r,
      ),
    );
  }
  function removeRow(setter, row) {
    if (row._new) {
      setter((rows) => rows.filter((r) => r.rowKey !== row.rowKey));
    } else {
      setter((rows) => rows.map((r) => (r.rowKey === row.rowKey ? { ...r, _deleted: true } : r)));
    }
  }
  function addRow(setter) {
    setter((rows) => [...rows, emptyRow()]);
  }

  // 행 단위 요청을 한꺼번에 보낸다. 반환: 실패 목록 + 성공 결과(Map<rowKey, {kind, value}>)
  async function persistList(rows, ops) {
    const jobs = [];
    const push = (r, kind, promise) => jobs.push({ rowKey: r.rowKey, kind, promise });
    for (const r of rows) {
      const payload = {
        name: r.name,
        title: r.title || null,
        phone: r.phone || null,
        email: r.email || null,
      };
      if (r._deleted && !r._new) {
        push(r, "del", ops.del(r.id));
      } else if (r._new && !r._deleted) {
        if (!r.name) push(r, "create", Promise.reject(new Error(t("partners.edit.nameRequired"))));
        else push(r, "create", ops.create(payload));
      } else if (r._dirty && !r._deleted) {
        if (!r.name) push(r, "update", Promise.reject(new Error(t("partners.edit.nameRequired"))));
        else push(r, "update", ops.update(r.id, payload));
      }
    }
    const results = await Promise.allSettled(jobs.map((j) => j.promise));
    const fails = [];
    const outcomes = new Map();
    results.forEach((x, i) => {
      if (x.status === "rejected") fails.push(x);
      else outcomes.set(jobs[i].rowKey, { kind: jobs[i].kind, value: x.value });
    });
    return { fails, outcomes };
  }

  async function handleSave() {
    if (saving) return;
    // 회사 이메일 — 지금은 표시·보관용(공지 알림은 계정 이메일로 간다). 형식이 틀리거나
    // 기존 값을 지우면 저장 전체를 중단하는 기존 규칙은 그대로 둔다.
    // 단, 원래 빈 값(NULL 레거시)이고 입력하지 않았으면 이메일 없이도 저장 허용
    const companyEmail = company.companyEmail.trim();
    const companyEmailUntouched = companyEmail === "" && initialCompanyEmail === "";
    if (companyDirty && !companyEmailUntouched && !EMAIL_RE.test(companyEmail)) {
      message.error(t("partners.edit.companyEmailInvalid"));
      return;
    }
    setSaving(true);
    try {
      const r1 = await persistList(salesReps, {
        create: createSalesRep,
        update: updateSalesRep,
        del: deleteSalesRep,
      });
      const r2 = await persistList(techSupports, {
        create: createTechSupport,
        update: updateTechSupport,
        del: deleteTechSupport,
      });
      const fails = [...r1.fails, ...r2.fails];
      let companySaved = false;
      // 회사 규모 — dirty 일 때만 PUT
      if (companyDirty) {
        try {
          await updateMyCompany({
            salesRepHeadcount: headcountToServer(company.salesRepHeadcount),
            techSupportHeadcount: headcountToServer(company.techSupportHeadcount),
            // 빈 값 그대로면 필드 생략 — 서버는 미전송 시 기존 값 유지
            ...(companyEmailUntouched ? {} : { companyEmail }),
          });
          companySaved = true;
        } catch (e) {
          fails.push({ reason: e });
        }
      }
      if (fails.length > 0) {
        const sample = fails[0].reason;
        const msg =
          (sample && sample.payload && sample.payload.error && sample.payload.error.message) ||
          (sample && sample.message) ||
          t("partners.edit.saveFailed");
        message.error(`${msg} (${fails.length})`);
        // 일부 실패 — 다시 불러오면 실패한 행·작성 중 내용까지 사라지므로 화면 상태를 유지하고
        // 성공한 요청만 확정한다(신규 행 id 확정 → 재저장 시 중복 생성 방지)
        setSalesReps((rows) => applySavedOutcomes(rows, r1.outcomes));
        setTechSupports((rows) => applySavedOutcomes(rows, r2.outcomes));
        if (companySaved) {
          setCompanyDirty(false);
          if (!companyEmailUntouched) setInitialCompanyEmail(companyEmail);
        }
      } else {
        message.success(t("partners.edit.saved"));
        await reload();
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-4xl">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {t("partners.edit.title")}
              </h1>
              <div className="flex flex-wrap gap-2">
                <Link to="/partners/dashboard" className="btw-btn-ghost">
                  {t("partners.edit.back")}
                </Link>
                <Button variant="accent" onClick={handleSave} disabled={saving || loading}>
                  {saving ? t("partners.deals.form.submitting") : t("partners.edit.save")}
                </Button>
              </div>
            </header>

            {loading ? (
              <p className="mt-10 text-center text-sm text-slate-400">
                {t("partners.common.loading")}
              </p>
            ) : (
              // 저장 중에는 편집 영역 전체를 막는다(fieldset disabled → 하위 입력·행 추가/삭제 버튼 일괄).
              // 요청을 보낸 뒤 고친 값이 일부 실패 시 applySavedOutcomes 로 "저장됨" 처리되는 경합 방지.
              <fieldset disabled={saving} className="mt-6 grid min-w-0 gap-6">
                <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.edit.sectionBasics")}
                  </h2>
                  <div className="mt-3 grid gap-4 md:grid-cols-2">
                    <Input
                      label={t("partners.signup.fields.companyName")}
                      value={basics.companyName}
                      disabled
                      inputClassName="disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    <Input
                      label={t("partners.signup.fields.businessNumber")}
                      value={basics.businessNumber}
                      disabled
                      inputClassName="disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    <Input
                      label={t("partners.signup.fields.representativeName")}
                      value={basics.representativeName}
                      disabled
                      inputClassName="disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    <Input
                      label={t("partners.signup.fields.industry")}
                      value={basics.industry}
                      disabled
                      inputClassName="disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    <Input
                      label={t("partners.signup.fields.address")}
                      value={basics.address}
                      disabled
                      className="md:col-span-2"
                      inputClassName="disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    <Input
                      label={t("partners.signup.fields.companyEmail")}
                      type="email"
                      required
                      value={company.companyEmail}
                      onChange={(e) => setCompanyField("companyEmail", e.target.value)}
                      maxLength={190}
                      hint={t("partners.edit.companyEmailHint")}
                      className="md:col-span-2"
                    />
                  </div>
                </section>
                <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.edit.sectionCompany")}
                  </h2>
                  <div className="mt-3 grid gap-4 md:grid-cols-2">
                    <Input
                      label={t("partners.signup.fields.salesRepHeadcount")}
                      placeholder={t("partners.signup.fields.headcountPlaceholder")}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={5}
                      value={company.salesRepHeadcount}
                      onChange={(e) =>
                        setCompanyField(
                          "salesRepHeadcount",
                          e.target.value.replace(/\D/g, "").slice(0, 5),
                        )
                      }
                    />
                    <Input
                      label={t("partners.signup.fields.techSupportHeadcount")}
                      placeholder={t("partners.signup.fields.headcountPlaceholder")}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={5}
                      value={company.techSupportHeadcount}
                      onChange={(e) =>
                        setCompanyField(
                          "techSupportHeadcount",
                          e.target.value.replace(/\D/g, "").slice(0, 5),
                        )
                      }
                    />
                  </div>
                </section>
                <RepSection
                  title={t("partners.edit.sectionSalesReps")}
                  addLabel={t("partners.edit.addSalesRep")}
                  rows={salesReps}
                  onChange={(rowKey, patch) => updateRow(setSalesReps, rowKey, patch)}
                  onRemove={(row) => removeRow(setSalesReps, row)}
                  onAdd={() => addRow(setSalesReps)}
                  hint={t("partners.edit.salesRepScopeHint")}
                />
                <RepSection
                  title={t("partners.edit.sectionTechSupports")}
                  addLabel={t("partners.edit.addTechSupport")}
                  rows={techSupports}
                  onChange={(rowKey, patch) => updateRow(setTechSupports, rowKey, patch)}
                  onRemove={(row) => removeRow(setTechSupports, row)}
                  onAdd={() => addRow(setTechSupports)}
                />
              </fieldset>
            )}
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}

function RepSection({ title, addLabel, rows, onChange, onRemove, onAdd, hint }) {
  const { t } = useTranslation();
  const visible = rows.filter((r) => !r._deleted);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
          {title}
        </h2>
        <button type="button" className="btw-btn-ghost" onClick={onAdd}>
          {addLabel}
        </button>
      </div>
      {hint && <p className="mt-2 px-1 text-xs text-slate-500">{hint}</p>}
      <div className="mt-4 grid gap-4">
        {visible.length === 0 ? (
          <p className="text-sm text-slate-400">{t("partners.edit.empty")}</p>
        ) : (
          visible.map((row) => (
            <div key={row.rowKey} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-3 md:grid-cols-2">
                <Input
                  label={t("partners.edit.rowName")}
                  required
                  value={row.name}
                  onChange={(e) => onChange(row.rowKey, { name: e.target.value })}
                  maxLength={50}
                />
                <Input
                  label={t("partners.edit.rowTitle")}
                  value={row.title}
                  onChange={(e) => onChange(row.rowKey, { title: e.target.value })}
                  maxLength={50}
                />
                <Input
                  label={t("partners.edit.rowPhone")}
                  value={row.phone}
                  onChange={(e) => onChange(row.rowKey, { phone: formatPhone(e.target.value) })}
                  maxLength={13}
                  inputMode="numeric"
                />
                <Input
                  label={t("partners.edit.rowEmail")}
                  type="email"
                  value={row.email}
                  onChange={(e) => onChange(row.rowKey, { email: e.target.value })}
                  maxLength={120}
                />
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  className="inline-flex h-9 items-center justify-center rounded-lg border border-rose-300 bg-white px-4 text-sm font-medium text-rose-600 transition hover:bg-rose-50"
                  onClick={() => onRemove(row)}
                >
                  {t("partners.edit.removeRow")}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
